import { uniq } from "lodash";
import type { VariableDB } from "@hightouch/lib/customer-data/decision-engine/types";
import {
  SYNC_CONCURRENCY_HELP,
  type DecisionEngineDestinationDefinition,
} from "./definition";
import {
  MOENGAGE_EVENT_NAME,
  toMoengageAttributeKey,
} from "./moengage-attribute-key";
import { getMappingForVariable } from "./sync-config-helpers";

/**
 * Attributes AID always sends, so the marketer can branch the flow on which
 * message fired it. Every static `to:` in `customMappings` below must appear
 * here, or a message variable of the same name silently overwrites it.
 */
const RESERVED_ATTRIBUTES = [
  "message_identifier",
  "ht_message_id",
  "agent_identifier",
  "channel",
];

/**
 * MoEngage event attributes are a flat key -> value map, and its Jinja
 * personalization indexes them by name with no documented way to index into a
 * nested object. So each AID message variable gets its own attribute rather
 * than the usual single `hightouch` blob. Message tags ride that blob, so they
 * are unavailable here — the same trade-off Airship makes.
 *
 * Under a channel-level sync this is the channel-wide union, but a row still
 * sends only the variables its own message defines: the Liquid lookup misses,
 * renders undefined, and the mapping layer drops undefined values. A variable
 * the message does define but leaves blank is sent as an empty attribute.
 *
 * Sanitization is many-to-one ("Discount.Code" and "Discount Code" both map to
 * discount_code), and a colliding key would silently overwrite one variable's
 * value with the other's. One sync feeds one attribute namespace, so a
 * collision across two messages is as broken as one within a message and the
 * check stays channel-wide. Config generation runs at enablement time, so
 * throwing surfaces the conflict to the user.
 */
function buildAttributeMappings(messageVariables: VariableDB[]) {
  const claimedBy = new Map<string, string>(
    RESERVED_ATTRIBUTES.map((key) => [key, "AI Decisioning"]),
  );

  return uniq(messageVariables.map((variable) => variable.name)).map((name) => {
    const key = toMoengageAttributeKey(name);
    const existing = claimedBy.get(key);
    if (existing !== undefined) {
      throw new Error(
        `MoEngage event attribute "${key}" is already claimed by ${existing}; rename the message variable "${name}". Attribute names are shared by every message in the channel, so the conflicting variable may belong to another message.`,
      );
    }
    claimedBy.set(key, `the message variable "${name}"`);
    return getMappingForVariable(name, key);
  });
}

export const moengageDefinition: DecisionEngineDestinationDefinition = {
  destinationType: "moengage",
  category: "event",
  generateSyncConfig: ({
    producer,
    scopeId,
    channelType,
    messageVariables = [],
  }) => ({
    type: "event",
    eventType: "user",
    eventName: MOENGAGE_EVENT_NAME,
    realTimeAction: true,
    externalIdMapping: { from: "external_id", to: "customer_id" },
    mappings: [],
    customMappings: [
      ...buildAttributeMappings(messageVariables),
      { type: "standard", from: "campaign_id", to: "message_identifier" },
      // Messages can share one campaign_id to reuse a MoEngage template, so
      // only the AID message id identifies which message actually sent.
      ...(producer === "journey"
        ? []
        : [{ type: "standard", from: "message_id", to: "ht_message_id" }]),
      {
        type: "static",
        value: scopeId,
        valueType: "STRING",
        to: "agent_identifier",
      },
      {
        type: "static",
        value: channelType,
        valueType: "STRING",
        to: "channel",
      },
    ],
  }),
  syncStrategy: {
    supportsChannelLevelSync: true,
  },
  syncTuning: {
    batchSize: {
      defaultValue: 100,
      configKey: "rowsPerBatch",
      max: 200,
      label: "Events per request",
      description: "Events sent together in each request to MoEngage.",
    },
    parallelization: {
      defaultValue: 10,
      max: 20,
      label: "Concurrent requests",
      description: "Controls how many event requests run in parallel.",
      help: SYNC_CONCURRENCY_HELP,
    },
  },
};
